/**
 * ZelofinLogo — design-system logo component.
 *
 * Variants:
 *  - "icon"       → standalone icon only (icon.png / dark variant not needed — icon is square)
 *  - "icon-text"  → horizontal logo with icon + text
 *  - "text"       → text-only wordmark
 *
 * Dark mode is class-based (.dark on <html>), so we swap images via Tailwind
 * `dark:hidden` / `hidden dark:block` utilities.
 *
 * forceDark prop — force the dark-background variant regardless of theme
 * (use inside sidebar which always has a dark bg).
 */

interface ZelofinLogoProps {
  variant?: 'icon' | 'icon-text' | 'text';
  /** Force the dark-variant logo (white text/icon) regardless of the active theme */
  forceDark?: boolean;
  className?: string;
}

const BASE = '/assets/images/logo/hq';

const SRCS = {
  'icon':      { light: `${BASE}/icon.png`,           dark: `${BASE}/icon.png` },
  'icon-text': { light: `${BASE}/icon-text-wide.png`, dark: `${BASE}/icon-text-wide-dark.png` },
  'text':      { light: `${BASE}/text-wide.png`,      dark: `${BASE}/text-wide-dark.png` },
} as const;

const ALT = 'Zelofin';

export function ZelofinLogo({ variant = 'icon-text', forceDark = false, className = '' }: ZelofinLogoProps) {
  const { light, dark } = SRCS[variant];

  if (forceDark || light === dark) {
    // Single image — no theme swapping needed
    return (
      <img
        src={forceDark ? dark : light}
        alt={ALT}
        className={className}
        draggable={false}
      />
    );
  }

  // Theme-aware: render both images, toggle visibility via Tailwind dark-mode utilities
  return (
    <>
      {/* Light theme */}
      <img
        src={light}
        alt={ALT}
        className={`dark:hidden ${className}`}
        draggable={false}
      />
      {/* Dark theme */}
      <img
        src={dark}
        alt={ALT}
        className={`hidden dark:block ${className}`}
        draggable={false}
      />
    </>
  );
}
